/* eslint-disable */
import { ref, watch, inject } from "vue";
import termService from "@/services/termService";
import { useLocaleStore } from "@/store/locale";

export function useFilterLogic(props, emit) {
  const notify = inject("notify");
  const localeStore = useLocaleStore();

  // Filter state
  const subject = ref([]);
  const grade = ref([]);
  const focus = ref([]);
  const hardware = ref([]);
  const duration = ref([]);
  const tags = ref([]);
  const popular = ref(false);
  const freeResources = ref(false);
  const selectedPopular = ref([]);
  const selectedFree = ref([]);

  // Filter options
  const subjectOptions = ref([]);
  const gradeOptions = ref([]);
  const focusOptions = ref([
    {
      value: 1,
      title: "Sustainability",
      text: "Sustainability",
      disabled: false,
    },
    {
      value: 2,
      title: "Engineering Design",
      text: "Engineering Design",
      disabled: false,
    },
    {
      value: 3,
      title: "Classic Maker",
      text: "Classic Maker",
      disabled: false,
    },
  ]);

  const durationOptions = ref([
    {
      value: "all",
      title: "All Duration",
      text: "All Duration",
      disabled: false,
      id: 0,
    },
    {
      value: 60,
      title: "60 minutes",
      text: "60 minutes",
      disabled: false,
      id: 1,
    },
    {
      value: 90,
      title: "90 minutes",
      text: "90 minutes",
      disabled: false,
      id: 2,
    },
    {
      value: 120,
      title: "120 minutes",
      text: "120 minutes",
      disabled: false,
      id: 3,
    },
  ]);
  const hardwareOptions = ref([]);
  const tagOptions = ref([]);
  const popularOptions = ref([
    { value: true, title: "Popular", text: "Popular", disabled: false, id: 1 },
  ]);
  const freeOptions = ref([
    { value: true, title: "Free", text: "Free", disabled: false, id: 1 },
  ]);

  // Filter mode state - true for expanded, false for hover
  const isExpandedMode = ref(false);
  const expandedInitialFilters = ref(null);

  const hasInitiallyFetched = ref(false);
  const pendingInitialEmit = ref(false);

  // Helper functions
  const matchFilterValues = (values, options) => {
    if (!values || values.length === 0) return [];

    return values
      .map((value) => {
        const match = options.find(
          (option) =>
            option.value === value ||
            option.title === value ||
            option.value?.toString() === value?.toString()
        );
        return match ? match.value : value;
      })
      .filter(Boolean);
  };

  const applyInitialFilters = () => {
    const initial = props.initialFilters;

    setTimeout(() => {
      // Reset all local selections first
      subject.value = [];
      grade.value = [];
      focus.value = [];
      hardware.value = [];
      duration.value = [];
      tags.value = [];
      popular.value = false;
      freeResources.value = false;
      selectedPopular.value = [];
      selectedFree.value = [];

      // Apply incoming selections
      if (initial.subjects && initial.subjects.length > 0) {
        subject.value = matchFilterValues(
          initial.subjects,
          subjectOptions.value
        );
      }
      if (initial.grades && initial.grades.length > 0) {
        grade.value = matchFilterValues(initial.grades, gradeOptions.value);
      }
      if (initial.focus && initial.focus.length > 0) {
        focus.value = matchFilterValues(initial.focus, focusOptions.value);
      }
      if (initial.hardware && initial.hardware.length > 0) {
        hardware.value = matchFilterValues(
          initial.hardware,
          hardwareOptions.value
        );
      }
      if (initial.duration && initial.duration.length > 0) {
        duration.value = matchFilterValues(
          initial.duration,
          durationOptions.value
        );
      }
      if (initial.tags && initial.tags.length > 0) {
        // Work with tag names directly
        const allTagNames = (tagOptions.value || [])
          .flatMap((c) => c.tag || [])
          .map((t) => t.tag);
        tags.value = initial.tags
          .map((t) => (allTagNames.includes(t) ? t : null))
          .filter(Boolean);
      }

      if (initial.popular !== undefined) {
        popular.value = Boolean(initial.popular);
        selectedPopular.value = popular.value ? [true] : [];
      }
      if (initial.free !== undefined) {
        freeResources.value = Boolean(initial.free);
        selectedFree.value = freeResources.value ? [true] : [];
      }

      // Mark pending emit and emit only after initial filter options are fetched
      pendingInitialEmit.value = true;
      if (hasInitiallyFetched.value) emitFilters();
    }, 0);
  };

  const fetchFilterData = async (locale, applyInitial = true) => {
    try {
      const localeToUse = locale || localeStore.currentLocale;

      const response = await termService.getAllFilterAirtable(localeToUse);
      if (response.status === 200) {
        const data = response.data.data;
        // Process tags into categorized format
        const categorizedTags = data.tags || [];
        tagOptions.value = categorizedTags;
        subjectOptions.value = [
          {
            value: "all",
            title: "All Subjects",
            text: "All Subjects",
            disabled: false,
            id: 0,
          },
          ...data.subjects
            .map((subject, index) => ({
              value: subject,
              title: subject,
              text: subject,
              disabled: false,
              id: index + 1,
            }))
            .sort((a, b) => a.title.localeCompare(b.title)),
        ];
        // Proses Grades
        let apiGrades = Array.isArray(data.grades) ? [...data.grades] : [];
        let gradeItems = apiGrades.map((grade, index) => ({
          value: grade,
          title: grade,
          text: grade,
          disabled: false,
          id: index + 1,
        }));

        gradeOptions.value = [
          {
            value: "all",
            title: "All Grades",
            text: "All Grades",
            disabled: false,
            id: 0,
          },
          ...gradeItems,
        ];
        focusOptions.value = [
          {
            value: "all",
            title: "All Focus",
            text: "All Focus",
            disabled: false,
            id: 0,
          },
          ...data.focus
            .map((focus, index) => ({
              value: focus,
              title: focus,
              text: focus,
              disabled: false,
              id: index + 1,
            }))
            .sort((a, b) => a.title.localeCompare(b.title)),
        ];
        hardwareOptions.value = [
          {
            value: "all",
            title: "All Hardware",
            text: "All Hardware",
            disabled: false,
            id: 0,
          },
          ...data.hardwareAssociateds
            .map((item, index) => ({
              value: index + 1,
              title: item.hardwareAssociated,
              count: item.count,
              disabled: false,
            }))
            .sort((a, b) => a.title.localeCompare(b.title)),
        ];
        if (applyInitial) {
          applyInitialFilters();
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getSelectedTagsFromCategories = () => {
    const selectedTags = [];
    tagOptions.value.forEach((category) => {
      if (category.tag) {
        category.tag.forEach((tag) => {
          if (tags.value.includes(tag.tag)) {
            selectedTags.push({
              id: tag.id,
              title: tag.tag,
              value: tag.tag,
              count: tag.count,
              category: category.category,
            });
          }
        });
      }
    });
    return selectedTags;
  };

  const emitFilters = () => {
    emit("filtersChanged", {
      subjects: subjectOptions.value.filter((option) =>
        subject.value.includes(option.value)
      ),
      grades: gradeOptions.value.filter((option) =>
        grade.value.includes(option.value)
      ),
      focus: focusOptions.value.filter((option) =>
        focus.value.includes(option.value)
      ),
      hardwareAssociated: hardwareOptions.value.filter((option) =>
        hardware.value.includes(option.value)
      ),
      hardware: hardwareOptions.value.filter((option) =>
        hardware.value.includes(option.value)
      ),
      duration: durationOptions.value.filter((option) =>
        duration.value.includes(option.value)
      ),
      tags: getSelectedTagsFromCategories(),
      popular: popular.value,
      free: freeResources.value,
    });
  };

  const emitFiltersFromPopup = (payload) => {
    if (payload.subjects) {
      subject.value = payload.subjects.map((item) => item.value);
    }
    if (payload.grades) {
      grade.value = payload.grades.map((item) => item.value);
    }
    if (payload.focus) {
      focus.value = payload.focus.map((item) => item.value);
    }
    if (payload.hardwareAssociated) {
      hardware.value = payload.hardwareAssociated.map((item) => item.value);
    }
    if (payload.duration) {
      duration.value = payload.duration.map((item) => item.value);
    }
    if (payload.tags) {
      tags.value = payload.tags.map((item) => item.id ?? item.value ?? item);
    }

    const completePayload = {
      ...payload,
      hardware: payload.hardwareAssociated,
      popular: popular.value,
      free: freeResources.value,
    };

    emit("filtersChanged", completePayload);
  };

  const handleAllSelection = (filterType, selectedValues, optionsRef) => {
    const filterMap = {
      subject: subject,
      grade: grade,
      focus: focus,
      duration: duration,
      hardware: hardware,
    };

    const currentFilter = filterMap[filterType];
    currentFilter.value = selectedValues;
  };

  const onSubjectChange = (payload) => {
    handleAllSelection("subject", payload.value, subjectOptions);
    emitFilters();
  };

  const onSubjectClear = () => {
    subject.value = [];
    emitFilters();
  };

  const onGradeChange = (payload) => {
    handleAllSelection("grade", payload.value, gradeOptions);
    emitFilters();
  };

  const onGradeClear = () => {
    grade.value = [];
    emitFilters();
  };

  const onDurationChange = (payload) => {
    handleAllSelection("duration", payload.value, durationOptions);
    emitFilters();
  };

  const onDurationClear = () => {
    duration.value = [];
    emitFilters();
  };

  const onHardwareChange = (payload) => {
    // Normalize any object values to their primitive value
    const values = (payload?.value || []).map((v) =>
      v?.value !== undefined ? v.value : v
    );
    hardware.value = values;
    emitFilters();
  };

  const onHardwareClear = () => {
    hardware.value = [];
    emitFilters();
  };

  const onTagsChange = (payload) => {
    // payload.value is array of tag names
    const next = Array.isArray(payload?.value) ? payload.value : [];
    tags.value = next;
    emitFilters();
  };

  const onTagsClear = () => {
    tags.value = [];
    emitFilters();
  };

  const onPopularDropdownChange = (payload) => {
    selectedPopular.value = payload.value || [];
    popular.value = selectedPopular.value.length > 0;
    emitFilters();
  };

  const onPopularDropdownClear = () => {
    selectedPopular.value = [];
    popular.value = false;
    emitFilters();
  };

  const onFreeDropdownChange = (payload) => {
    selectedFree.value = payload.value || [];
    freeResources.value = selectedFree.value.length > 0;
    emitFilters();
  };

  const onFreeDropdownClear = () => {
    selectedFree.value = [];
    freeResources.value = false;
    emitFilters();
  };

  const toggleFilterMode = () => {
    // when going to expanded mode, snapshot current dropdown selections as initial filters
    if (!isExpandedMode.value) {
      expandedInitialFilters.value = {
        subjects: [...subject.value],
        grades: [...grade.value],
        focus: [...focus.value],
        hardware: [...hardware.value],
        duration: [...duration.value],
        tags: [...tags.value],
        sort: props.initialFilters.sort || "newest",
        locale: props.locale || "US",
        popular: Boolean(popular.value),
        free: Boolean(freeResources.value),
      };
    } else {
      // Leaving expanded mode, drop snapshot so external changes propagate next time
      expandedInitialFilters.value = null;
    }
    isExpandedMode.value = !isExpandedMode.value;
    emit("modeChanged", isExpandedMode.value ? "expanded" : "compact");
  };

  const onExpandedFiltersChanged = (dataFilter) => {
    // Update local values from expanded filter
    subject.value = dataFilter.subjects?.map((s) => s.value) || [];
    grade.value = dataFilter.grades?.map((g) => g.value) || [];
    hardware.value = dataFilter.hardwareAssociated?.map((h) => h.value) || [];
    duration.value = dataFilter.duration?.map((d) => d.value) || [];
    tags.value = dataFilter.tags?.map((t) => t.value) || [];
    popular.value = dataFilter.popular || false;
    freeResources.value = dataFilter.free || false;

    // Update dropdowns for consistency
    selectedPopular.value = popular.value ? [true] : [];
    selectedFree.value = freeResources.value ? [true] : [];

    // Emit the filters to parent
    emit("filtersChanged", dataFilter);
  };

  // Watch for initial filters changes
  watch(
    () => props.initialFilters,
    (newFilters) => {
      if (newFilters && Object.keys(newFilters).length > 0) {
        // Reset snapshot so expanded view follows parent updates
        expandedInitialFilters.value = null;
        applyInitialFilters();
      }
    },
    { deep: true }
  );

  // Watch for locale changes
  watch(
    () => props.locale,
    async (newLocale, oldLocale) => {
      if (newLocale) {
        if (!hasInitiallyFetched.value) {
          // Resolve initial locale preferring store value on first load
          const initialLocale = newLocale || localeStore.currentLocale;
          await fetchFilterData(initialLocale, true);
          hasInitiallyFetched.value = true;
          // If an initial emit was queued (e.g., from initialFilters watcher), emit now
          if (pendingInitialEmit.value) {
            emitFilters();
            pendingInitialEmit.value = false;
          }
          return;
        }

        if (oldLocale && oldLocale !== newLocale) {
          const previousSelectedHardwareTitles = hardwareOptions.value
            .filter((option) => hardware.value.includes(option.value))
            .map((option) => option.title);

          const hadResettableFilters =
            (subject.value && subject.value.length > 0) ||
            (grade.value && grade.value.length > 0) ||
            (selectedPopular.value && selectedPopular.value.length > 0) ||
            Boolean(popular.value);

          subject.value = [];
          grade.value = [];
          selectedPopular.value = [];
          popular.value = false;

          await fetchFilterData(newLocale, false);

          if (
            hadResettableFilters &&
            notify &&
            typeof notify.plain === "function"
          ) {
            notify.plain("Some filters were reset for this locale");
          }

          if (
            previousSelectedHardwareTitles &&
            previousSelectedHardwareTitles.length > 0
          ) {
            hardware.value = hardwareOptions.value
              .filter((option) =>
                previousSelectedHardwareTitles.includes(option.title)
              )
              .map((option) => option.value);
          }

          emitFilters();
        }
      }
    },
    { immediate: true }
  );

  return {
    // State
    subject,
    grade,
    focus,
    hardware,
    duration,
    tags,
    popular,
    freeResources,
    selectedPopular,
    selectedFree,
    isExpandedMode,
    expandedInitialFilters,

    // Options
    subjectOptions,
    gradeOptions,
    focusOptions,
    durationOptions,
    hardwareOptions,
    tagOptions,
    popularOptions,
    freeOptions,

    // Methods
    applyInitialFilters,
    fetchFilterData,
    emitFilters,
    emitFiltersFromPopup,
    onSubjectChange,
    onSubjectClear,
    onGradeChange,
    onGradeClear,
    onDurationChange,
    onDurationClear,
    onHardwareChange,
    onHardwareClear,
    onTagsChange,
    onTagsClear,
    onPopularDropdownChange,
    onPopularDropdownClear,
    onFreeDropdownChange,
    onFreeDropdownClear,
    toggleFilterMode,
    onExpandedFiltersChanged,
  };
}
